/**
 * Kinetipath FAQ — Anchor nav.
 *
 * Responsibilities:
 *   1. SCROLL-SPY — highlight the pill of the category currently in
 *      view using IntersectionObserver.
 *   2. STICKY-STACK OFFSET — measure faq-search section height and
 *      set --faq-nav-sticky-top so this nav docks below the search bar.
 *   3. SCROLL PADDING — set --faq-scroll-padding on <html> so anchor
 *      jumps land below the sticky stack instead of behind it.
 *   4. PILL CLICKS — set active immediately and scroll the active pill
 *      into view in the horizontal strip on mobile.
 *   5. OVERFLOW INDICATOR — set data-has-overflow on the nav so the
 *      right-edge gradient fade only shows when scrolling is possible.
 *
 * Initialised on DOMContentLoaded. Bails silently if the nav section
 * is not on the current page.
 */
(function () {
  'use strict';

  function init() {
    var nav = document.querySelector('[data-faq-anchor-nav]');
    if (!nav) return;

    var pills = nav.querySelectorAll('[data-faq-pill]');
    var search = document.querySelector('[data-faq-search-section]');
    var scroller = nav.querySelector('.faq-anchor-nav__scroller');
    var categories = document.querySelectorAll('[data-faq-category][id]');

    function setActive(slug) {
      for (var i = 0; i < pills.length; i++) {
        var isMatch = pills[i].dataset.anchor === slug;
        pills[i].dataset.active = isMatch ? 'true' : 'false';
        if (isMatch && scroller && scroller.scrollWidth > scroller.clientWidth) {
          // Centre the active pill in the horizontal scroller.
          var pillRect = pills[i].getBoundingClientRect();
          var scRect = scroller.getBoundingClientRect();
          var offset = pillRect.left - scRect.left - (scRect.width - pillRect.width) / 2;
          scroller.scrollBy({ left: offset, behavior: 'smooth' });
        }
      }
    }

    function isSticky(el) {
      if (!el) return false;
      return window.getComputedStyle(el).position === 'sticky';
    }

    function recalcStickyStack() {
      // After the structural fix, position: sticky lives on the
      // Shopify section wrapper (.shopify-section.faq-*-section), not on
      // the inner <section data-faq-*>. So measure the wrappers and set
      // the --faq-nav-sticky-top custom property on :root so the wrapper's
      // CSS rule can pick it up.
      var searchWrap = search ? search.closest('.shopify-section') : null;
      var navWrap = nav.closest('.shopify-section');
      var searchH = (searchWrap && isSticky(searchWrap))
        ? searchWrap.getBoundingClientRect().height
        : 0;
      var navH = (navWrap && isSticky(navWrap))
        ? navWrap.getBoundingClientRect().height
        : 0;
      document.documentElement.style.setProperty('--faq-nav-sticky-top', searchH + 'px');
      document.documentElement.style.setProperty(
        '--faq-scroll-padding',
        (searchH + navH + 16) + 'px'
      );
    }

    function recalcOverflow() {
      if (!scroller) return;
      var hasOverflow = scroller.scrollWidth > scroller.clientWidth;
      nav.dataset.hasOverflow = hasOverflow ? 'true' : 'false';
    }

    recalcStickyStack();
    recalcOverflow();
    window.addEventListener('resize', function () {
      recalcStickyStack();
      recalcOverflow();
    });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        recalcStickyStack();
        recalcOverflow();
      });
      if (search) ro.observe(search);
      ro.observe(nav);
      if (scroller) ro.observe(scroller);
    }

    // Pill clicks
    for (var p = 0; p < pills.length; p++) {
      (function (pill) {
        pill.addEventListener('click', function () {
          setActive(pill.dataset.anchor);
        });
      })(pills[p]);
    }

    // Scroll-spy
    if (nav.dataset.scrollSpy === 'true' && categories.length && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        var bestEntry = null;
        var bestRatio = 0;
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting && entries[i].intersectionRatio > bestRatio) {
            bestEntry = entries[i];
            bestRatio = entries[i].intersectionRatio;
          }
        }
        if (bestEntry) setActive(bestEntry.target.id);
      }, {
        rootMargin: '-30% 0px -50% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1]
      });
      for (var c = 0; c < categories.length; c++) observer.observe(categories[c]);
    }

    // Initial active state from URL hash, else first pill
    if (location.hash) {
      setActive(location.hash.slice(1));
    } else if (pills.length) {
      setActive(pills[0].dataset.anchor);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
